import { initDiscoveryClient } from "@/api/apiClient";
import {
  getCanvasParticipationView,
  getMyCanvasParticipationVotes,
  postCanvasParticipationComplete,
  postCanvasParticipationPing,
  putCanvasParticipationVotes,
} from "@/api/openapi/discovery/sdk.gen";
import type { CanvasParticipationView, VoteBatchItemIn, VoteMeOut } from "@/api/openapi/discovery/types.gen";
import { ApiError, normalizeCanvasData, toApiError, unwrapClientData } from "@/services/api";
import type { CanvasData } from "@/services/apitypes";

export type ParticipationIdentity = { voterId?: string; authToken?: string };

function participationHeaders(identity: ParticipationIdentity) {
  return {
    ...(identity.voterId ? { "X-Anonymous-Voter-Id": identity.voterId } : {}),
    ...(identity.authToken ? { Authorization: `Bearer ${identity.authToken}` } : {}),
  };
}

function assertSuccessfulResponse(response: { error?: unknown; response?: Response }, fallbackMessage: string): void {
  if (!response.error) return;
  const detail =
    typeof response.error === "object" && response.error !== null && "detail" in response.error
      ? String(response.error.detail)
      : fallbackMessage;
  throw new ApiError(detail, response.response?.status ?? 500, response.error);
}

export async function getParticipationCanvas(
  token: string,
  language?: string,
  authToken?: string,
): Promise<CanvasData> {
  try {
    const response = await getCanvasParticipationView({
      client: initDiscoveryClient(),
      path: { token },
      query: language ? { language } : undefined,
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      throwOnError: false,
    });
    assertSuccessfulResponse(response, "Failed to load the participation link.");
    const session = unwrapClientData<CanvasParticipationView>(response);
    if (!session?.canvas) throw new ApiError("No participation data returned.", 500);
    return normalizeCanvasData(session.canvas);
  } catch (error) {
    throw toApiError(error, "Failed to load the participation link.");
  }
}

export async function getParticipationVotes(token: string, identity: ParticipationIdentity): Promise<VoteMeOut[]> {
  try {
    const response = await getMyCanvasParticipationVotes({
      client: initDiscoveryClient(),
      path: { token },
      headers: participationHeaders(identity),
      throwOnError: false,
    });
    assertSuccessfulResponse(response, "Failed to load votes.");
    return unwrapClientData<VoteMeOut[]>(response);
  } catch (error) {
    throw toApiError(error, "Failed to load votes.");
  }
}

export async function putParticipationVotes(
  token: string,
  identity: ParticipationIdentity,
  votes: VoteBatchItemIn[],
): Promise<void> {
  try {
    const response = await putCanvasParticipationVotes({
      client: initDiscoveryClient(),
      path: { token },
      headers: participationHeaders(identity),
      body: { votes },
      throwOnError: false,
    });
    assertSuccessfulResponse(response, "Failed to update votes.");
  } catch (error) {
    throw toApiError(error, "Failed to update votes.");
  }
}

export async function pingParticipation(token: string, identity: ParticipationIdentity): Promise<void> {
  try {
    const response = await postCanvasParticipationPing({
      client: initDiscoveryClient(),
      path: { token },
      headers: participationHeaders(identity),
      throwOnError: false,
    });
    assertSuccessfulResponse(response, "Failed to update participation activity.");
  } catch (error) {
    throw toApiError(error, "Failed to update participation activity.");
  }
}

export async function completeParticipation(token: string, identity: ParticipationIdentity): Promise<void> {
  try {
    const response = await postCanvasParticipationComplete({
      client: initDiscoveryClient(),
      path: { token },
      headers: participationHeaders(identity),
      throwOnError: false,
    });
    assertSuccessfulResponse(response, "Failed to submit priorities.");
  } catch (error) {
    throw toApiError(error, "Failed to submit priorities.");
  }
}
